<template>
  <!--the calc is screenhight - headerhight -->
  <section class="relative h-[calc(100svh-4.25rem)] md:h-[calc(100svh-8rem)]">
    <!-- Background Image with overlay -->
    <div class="absolute inset-0 -z-10 overflow-hidden">
      <div
        v-if="heroData"
        class="flex h-full w-full transition-transform duration-[1s] ease-[cubic-bezier(0.770,0.000,0.175,1.000)] will-change-transform"
        :style="{ transform: `translateX(-${activeTab * 100}%)` }"
      >
        <div
          v-for="(slide, index) in heroData.slides"
          :key="`slide-${index}`"
          class="h-full min-w-full"
        >
          <img
            class="size-full object-cover"
            :src="slide.image.src"
            :alt="slide.image.alt"
            :style="slide.image.focalStyle || undefined"
          />
        </div>
      </div>
    </div>

    <!-- Content Container -->
    <div
      class="absolute inset-0 -z-10 w-[100vw] bg-gradient-to-t from-black to-transparent to-60% m:to-80%"
    ></div>

    <div
      class="relative mx-auto flex h-full max-w-content flex-col justify-end px-sidespacing xl:max-w-full"
    >
      <!-- Title and Buttons Row -->
      <div
        class="mb-8 flex flex-row flex-wrap items-center justify-between xl:mb-[2vw]"
      >
        <h1
          v-if="heroData"
          class="animate-fade mr-8 text-secondary xl:mr-[2vw]"
        >
          {{ heroData.heading }}
        </h1>

        <div
          v-if="heroData"
          class="flex flex-row flex-wrap gap-8 xm:gap-0 xl:gap-[2vw]"
        >
          <btn
            v-for="(button, index) in heroData.buttons"
            :key="index"
            :text="button.title"
            :url="button.url"
            :type="button.variant"
            :target="button.target"
            class="animate-fade"
          >
          </btn>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div
        v-if="heroData"
        role="tablist"
        aria-orientation="horizontal"
        class="flex justify-center gap-4 pb-8"
      >
        <button
          v-for="(slide, index) in heroData.slides"
          :key="`tab-${index}`"
          type="button"
          role="tab"
          :aria-selected="activeTab === index"
          :data-state="activeTab === index ? 'active' : 'inactive'"
          class="animate-slide-left relative px-8 py-4 text-center text-white"
          :class="
            activeTab === index ? 'flex-1' : 'flex-[0.2] m:px-0 md:flex-1'
          "
          @animationend="handleAnimationEnd(index)"
          @click="setActiveTab(index)"
        >
          <span :class="{ 'hidden md:inline': activeTab !== index }">{{
            slide.heading
          }}</span>
          <div class="absolute inset-x-0 bottom-0 h-1 bg-white/20">
            <div
              class="h-full bg-white transition-all duration-300"
              :class="{ 'animate-progress': activeTab === index }"
              :style="{ width: activeTab === index ? '100%' : '0%' }"
            ></div>
          </div>
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* tab-bar bottom */
.animate-progress {
  animation: progress 8s ease-in-out forwards;
}

@keyframes progress {
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
}
</style>

<script setup lang="ts">
import { ref, computed } from 'vue';
import btn from './button.vue';

// Types
interface ImageProps {
  src: string;
  alt?: string;
  focalStyle?: string;
}

interface ButtonProps {
  title: string;
  url?: string;
  variant?: 'primary' | 'secondary-alt';
}

interface HeroData {
  heading: string;
  buttons: ButtonProps[];
  slides: {
    heading: string;
    image: ImageProps;
  }[];
}

interface Props {
  heroData?: HeroData;
}

// Props
const props = defineProps<Props>();

// Reactive state
const activeTab = ref(0);

// Computed
const activeContent = computed(() => {
  return props.heroData?.slides[activeTab.value]?.image;
});

// Methods
const setActiveTab = (index: number) => {
  activeTab.value = index;
};

const handleAnimationEnd = (index: number) => {
  if (activeTab.value === index) {
    const nextIndex = (index + 1) % (props.heroData?.slides.length || 1);
    activeTab.value = nextIndex;
  }
};
</script>
